/**
 * Draft state for the grid configuration dialog.
 *
 * The dialog edits a working copy and commits nothing until the user saves, so
 * order and visibility are held together in one ordered list of rows rather
 * than as two parallel structures that could disagree. This module owns every
 * transition on that list, keeping the dialog itself free of state arithmetic.
 *
 * Columns and stat cards are configured the same way and share these
 * transitions: each is a list of identified, labelled, individually hideable
 * things whose order the user may rearrange. A locked item (`hideable: false`)
 * can be moved but never hidden, and is left out of the saved visibility model
 * entirely — the grid forces it visible from the descriptor regardless, so
 * storing a decision about it would be a preference nothing reads.
 *
 * A pinned column (`isMovable: false`) is the mirror case: it may be hidden if
 * the descriptor allows, but never moved. Where it sits is a property of the
 * pin, which the product does not offer the user, and dragging one would
 * promise a rearrangement the grid then ignores.
 *
 * Contains:
 * - buildDraftRows — items + saved visibility → ordered draft rows
 * - toggleDraftVisibility — flip one row's shown state
 * - moveDraftRow — reposition one row onto another's index
 * - isDraftDirty — whether the draft differs from what is saved
 * - draftToPreferences — draft rows → the two values to persist
 *
 * Returns plain JS — no JSX, no redux.
 */

/**
 * Whether an item counts as shown under a saved visibility model. Only an
 * explicit `false` hides one, matching both the grid's own card read and MUI's
 * column-visibility vocabulary, so a model that has never heard of an item
 * leaves it shown.
 *
 * @param {object} visibilityModel - `{ [id]: boolean }`, possibly empty
 * @param {{id: string, hideable: boolean}} item
 * @returns {boolean}
 */
function isItemShown(visibilityModel, item) {
  if (item.hideable === false) return true;
  return visibilityModel?.[item.id] !== false;
}

/**
 * @param {Array<{id: string, label: string, hideable: boolean, isMovable: (boolean|undefined)}>} items -
 *   columns or stat cards, already in the order the grid renders them
 * @param {object} visibilityModel - the saved `{ [id]: boolean }` model
 * @returns {Array<{id: string, label: string, hideable: boolean, isMovable: boolean, isShown: boolean, description: (string|undefined)}>}
 */
export function buildDraftRows(items, visibilityModel) {
  if (!Array.isArray(items)) return [];
  return items.map(item => ({
    id: item.id,
    label: item.label,
    hideable: item.hideable !== false,
    isMovable: item.isMovable !== false,
    isShown: isItemShown(visibilityModel, item),
    description: item.description,
  }));
}

/**
 * @param {Array<object>} rows
 * @param {string} id
 * @returns {Array<object>} A new list; a locked row is returned untouched.
 */
export function toggleDraftVisibility(rows, id) {
  return rows.map(row => (row.id === id && row.hideable ? { ...row, isShown: !row.isShown } : row));
}

/**
 * Move the row identified by `activeId` to the index currently held by
 * `overId`, sliding the rows between them along by one.
 *
 * A pinned row is refused as both the thing moved and the place moved to, so
 * the pinned block keeps its position however the drag was aimed.
 *
 * @param {Array<object>} rows
 * @param {string} activeId - the dragged row
 * @param {string} overId   - the row it was dropped onto
 * @returns {Array<object>} A new list, or `rows` itself when the move is a no-op.
 */
export function moveDraftRow(rows, activeId, overId) {
  if (activeId === overId) return rows;
  const from = rows.findIndex(row => row.id === activeId);
  const to = rows.findIndex(row => row.id === overId);
  if (from === -1 || to === -1) return rows;
  if (rows[from].isMovable === false || rows[to].isMovable === false) return rows;
  const next = [...rows];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/**
 * @param {Array<object>} rows - the draft
 * @param {Array<object>} items - the items the draft was built from
 * @param {object} visibilityModel - the saved model the draft was built from
 * @returns {boolean} True when saving would change something.
 */
export function isDraftDirty(rows, items, visibilityModel) {
  const saved = buildDraftRows(items, visibilityModel);
  if (rows.length !== saved.length) return true;
  return rows.some((row, index) => row.id !== saved[index].id || row.isShown !== saved[index].isShown);
}

/**
 * Flatten a draft into the two preference values the grid reads back.
 *
 * Every hideable item gets an explicit boolean rather than being omitted when
 * shown: the descriptor's own `default_hidden` is merged underneath the saved
 * model, so an absent key would let a default-hidden item spring back the next
 * time the page loads.
 *
 * @param {Array<object>} rows
 * @returns {{order: Array<string>, visibilityModel: Object<string, boolean>}}
 */
export function draftToPreferences(rows) {
  const visibilityModel = {};
  for (const row of rows) {
    if (row.hideable) visibilityModel[row.id] = row.isShown;
  }
  return { order: rows.map(row => row.id), visibilityModel };
}
